import { Box, Button, Typography, useTheme } from '@mui/material'
import {
  DateCalendar,
  DayCalendarSkeleton,
  LocalizationProvider,
  PickersDay,
  PickersDayProps,
} from '@mui/x-date-pickers-pro'
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs'
import { RoleEnum } from '@shared/Enums'
import { EthAddr } from '@shared/SchemaTypes'

import dayjs, { Dayjs } from 'dayjs'
import React, { useEffect, useState } from 'react'
import { useOtChainState, useOtSelector } from '@react/redux/store'

import dayOfYear from 'dayjs/plugin/dayOfYear'
import { logOT } from '@shared/loggingShared'
import { updateFlexPoolNonBusinessDays } from '@react/signedTxs/updateFlexPoolNonBusinessDaysTx'
import { PoolFlexOverviewCTO } from '@shared/PoolFlexCTO'
dayjs.extend(dayOfYear)

interface Props {
  poolAddr: EthAddr
  role: RoleEnum
  startInUpdate?: boolean
}

export const PoolFlexCalendar: React.FC<Props> = ({ poolAddr, role, startInUpdate = false }) => {
  const {
    webSocket,
    userConnection: { signer },
    chainConfiguration: { chainConfigurationName, providerReadOnly },
  } = useOtChainState()

  const poolOverview = useOtSelector((state) => state.poolState.poolOverviewCTOs[poolAddr]) as PoolFlexOverviewCTO

  const theme = useTheme()

  const [loading, setLoading] = useState(false)
  const [highlightedDays, setHighlightedDays] = useState<Dayjs[]>([])
  const [currentUpdateDays, setCurrentUpdateDays] = useState<Dayjs[]>([])

  const [isUpdate, setIsUpdate] = useState(startInUpdate)

  useEffect(() => {
    async function fetchDays() {
      if (!providerReadOnly || !poolOverview) return

      const hds = poolOverview.nonBusinessDays?.map((d: number) => {
        const date = new Date((d ?? 0) * 1000)
        return dayjs(`${date.getUTCFullYear()}-${date.getUTCMonth() + 1}-${date.getUTCDate()}`)
      }) as Dayjs[]

      setHighlightedDays(hds)
      setLoading(false)
    }

    fetchDays()
  }, [providerReadOnly])

  const handleDayClick = (e: Dayjs) => {
    if (!isUpdate) return
    const dateIndex = currentUpdateDays.findIndex((d) => e.isSame(d))

    if (dateIndex > -1) {
      setCurrentUpdateDays([...currentUpdateDays.filter((_v, idx) => idx !== dateIndex)])
    } else {
      setCurrentUpdateDays([...currentUpdateDays, e])
    }
  }

  const onSave = async (): Promise<void> => {
    if (!signer || !poolAddr) return
    try {
      await updateFlexPoolNonBusinessDays(webSocket, signer, poolAddr, currentUpdateDays, chainConfigurationName)
      setHighlightedDays([...highlightedDays, ...currentUpdateDays])
      setCurrentUpdateDays([])
    } catch (ex) {
      logOT.info('Failed to update, ex: ', ex)
    }
  }

  const onUpdate = async () => {
    if (isUpdate) {
      setIsUpdate(false)
      setLoading(true)
      await onSave()
      setLoading(false)
    } else {
      setIsUpdate(true)
    }
  }

  function NonBusinessDay(props: PickersDayProps<Dayjs> & { highlightedDays?: Dayjs[] }) {
    const { highlightedDays = [], day, outsideCurrentMonth, ...other } = props
    const isWeekend = props.day.day() === 0 || props.day.day() === 6

    const isSelected =
      (!props.outsideCurrentMonth &&
        highlightedDays.find((d) => {
          return props.day.isSame(d)
        })) ||
      isWeekend

    const isDraft = currentUpdateDays.find((d) => day.isSame(d))
    const backgroundColor = isSelected ? theme.palette.info.muted : isDraft ? theme.palette.accent1.muted : 'white'
    return <PickersDay {...other} outsideCurrentMonth={outsideCurrentMonth} day={day} style={{ backgroundColor }} />
  }

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          flexDirection: 'row',
          paddingLeft: 20,
          paddingRight: 20,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-start' }}>
          <Box style={{ width: 20, height: 20, backgroundColor: theme.palette.info.muted, borderRadius: 20 }}></Box>
          <Typography sx={{ marginLeft: 2 }} variant="body">
            Non Business day
          </Typography>
        </Box>
        {role === RoleEnum.POOL_ADMIN && (
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          <Button variant="contained" onClick={onUpdate}>
            {isUpdate ? 'Save' : 'Update'}
          </Button>
        )}
      </Box>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DateCalendar
          loading={loading}
          renderLoading={() => <DayCalendarSkeleton />}
          slots={{
            day: NonBusinessDay,
          }}
          onChange={(e) => {
            if (!e) return
            handleDayClick(e)
          }}
          slotProps={{
            day: {
              highlightedDays,
            } as any,
          }}
        />
      </LocalizationProvider>
    </>
  )
}
